import {useEffect, useState} from 'react';
import {useQueries} from '@tanstack/react-query';
import {
  ApplicableContract,
  Contract,
  Shipment,
  ShipmentPreferredCurrencyEnum
} from '@shipwell/backend-core-singlerequestparam-sdk';
import invariant from 'tiny-invariant';
import {useSelectedContract, isContract} from '../contracts/useSelectedContract';
import {CARRIER_CAPACITY_AVAILABILITY_QUERY_KEY, CARRIER_RELATIONSHIPS_QUERY_KEY} from 'App/data-hooks/queryKeys';
import {getCarrierCapacityAvailability} from 'App/api/carriers/carrierCapacity';
import {formatCarrierPOCs} from 'App/utils/carrierHelpers';
import {getCarrierRelationshipsByIdPromise} from 'App/api/carriers/carrierRelationship';
import {getStopOriginDestinationAndAdditionalAddresses} from 'App/formComponents/formSections/tenderRequest/utils';

interface BulkTenderContract {
  shipments: Shipment[];
  shipment?: never;
  percentOfLineHaul?: number;
}

interface SingleTenderContract {
  shipment: Shipment;
  shipments?: never;
  percentOfLineHaul?: number;
}

type TenderContract = Contract & Partial<{additional_stops: object[]}>;

export const useTenderContract = ({
  shipments,
  shipment,
  percentOfLineHaul
}: BulkTenderContract | SingleTenderContract) => {
  const [selectedContracts, setSelectedContracts] = useState<TenderContract[]>([]);
  const [capacityAvailable, setCapacityAvailable] = useState(true);

  const getSingleShipmentStops = () => {
    if (typeof shipment !== 'undefined') {
      return shipment.stops || [];
    }
    if (Array.isArray(shipments) && shipments.length === 1) {
      return shipments[0].stops || [];
    }
    return [];
  };

  const distance = (shipment ? shipment.total_miles : shipments[0].total_miles) || 0;
  const isBulkShipments = !!shipments;
  const additionalStops = getStopOriginDestinationAndAdditionalAddresses(getSingleShipmentStops()).additionalStops
    ?.length;

  const {
    selectedContract,
    handleContractChange,
    fuelSurchargeRate,
    isLoading,
    contractCarrierRelationship,
    chargeLineItems
  } = useSelectedContract({percentOfLineHaul, distance, isBulkShipments, additionalStops});

  // used for the following carrier capacity queries
  const shipmentIds = shipment ? [shipment.id] : shipments?.map((shipment) => shipment.id);
  // we now allow users to select multiple contracts
  const contractIds = selectedContract ? [selectedContract.id] : selectedContracts?.map((contract) => contract.id);

  const carrierCapacityAvailableBulkQueries = useQueries({
    queries: shipmentIds?.flatMap((id) =>
      contractIds.map((contractId) => ({
        queryKey: [CARRIER_CAPACITY_AVAILABILITY_QUERY_KEY, id, contractId],
        queryFn: () => getCarrierCapacityAvailability({shipmentId: id, contractId: contractId || ''}),
        enabled: !!contractIds.length && !!shipmentIds.length
      }))
    )
  });

  useEffect(() => {
    if (carrierCapacityAvailableBulkQueries.every((query) => query.isSuccess)) {
      setCapacityAvailable(carrierCapacityAvailableBulkQueries.every((query) => query.data?.data.capacity_available));
    }
  }, [carrierCapacityAvailableBulkQueries]);

  // used when muliple contracts are selected
  const carrierRelationshipsQueries = useQueries({
    queries: selectedContracts.map((contract) => ({
      queryKey: [CARRIER_RELATIONSHIPS_QUERY_KEY, contract?.carrier_relationship],
      queryFn: () => getCarrierRelationshipsByIdPromise(contract?.carrier_relationship || ''),
      enabled: !!selectedContracts.length
    }))
  });

  const getShipmentEquipmentAndMode = () => {
    let equipmentType;
    let mode;
    if (!shipment) {
      const equipmentTypes = shipments.map((shipment) => shipment.equipment_type?.machine_readable);
      assertAllEqual(equipmentTypes, 'equipment_type');
      equipmentType = equipmentTypes[0];

      const modes = shipments.map((shipment) => shipment.mode?.id);
      assertAllEqual(modes, 'mode');
      mode = modes[0];

      const preferredCurrencies = shipments.map((shipment) => shipment.preferred_currency);
      assertAllEqual(preferredCurrencies, 'preferred_currency');
    } else {
      equipmentType = shipment.equipment_type?.machine_readable;
      mode = shipment.mode?.id;
    }

    return {equipmentType, mode};
  };

  //returns a single payload object if tendering using a single contract
  const getContractValues = (formValues?: {
    expires_at?: string;
    expires_after_seconds?: string;
    info_message: string;
    contractRateCurrency?: ShipmentPreferredCurrencyEnum;
  }) => {
    const {equipmentType, mode} = getShipmentEquipmentAndMode();

    const payload = {
      contract: selectedContract?.id,
      equipment_type: equipmentType,
      ...(formValues?.expires_at ? {expires_at: formValues.expires_at} : {}),
      ...(formValues?.expires_after_seconds ? {expires_after_seconds: formValues.expires_after_seconds} : {}),
      info_message: formValues?.info_message || '',
      message: '',
      mode,
      shipment: shipmentIds,
      tender_to_company: formatCarrierPOCs([contractCarrierRelationship])
    };
    return payload;
  };

  //returns an array of payloads to POST if tendering using multiple contracts
  const getMultiContractsValues = (formValues?: {
    expires_at?: string;
    expires_after_seconds?: string;
    info_message: string;
    contractRateCurrency?: ShipmentPreferredCurrencyEnum;
  }) => {
    const {equipmentType, mode} = getShipmentEquipmentAndMode();

    const payloads = selectedContracts.map((contract, index) => ({
      contract: contract.id,
      equipment_type: equipmentType,
      ...(formValues?.expires_at ? {expires_at: formValues.expires_at} : {}),
      ...(formValues?.expires_after_seconds ? {expires_after_seconds: formValues.expires_after_seconds} : {}),
      info_message: formValues?.info_message || '',
      message: '',
      mode,
      shipment: shipmentIds,
      tender_to_company: formatCarrierPOCs([carrierRelationshipsQueries[index].data?.data])
    }));
    return payloads;
  };

  return {
    selectedContract,
    selectedContracts,
    handleContractChange: (value: (Contract | ApplicableContract) | (Contract | ApplicableContract)[]) =>
      Array.isArray(value)
        ? setSelectedContracts(
            value
              .map((value) => (isContract(value) ? value : value.contract))
              ?.filter((value): value is Contract => !!value)
          )
        : handleContractChange(value),
    fuelSurchargeRate,
    isLoading:
      isLoading ||
      carrierRelationshipsQueries.some((query) => query.isFetching) ||
      carrierCapacityAvailableBulkQueries.some((query) => query.isFetching),
    carrierCapacityIsAvailable: capacityAvailable,
    getContractValues,
    getMultiContractsValues,
    contractCarrierRelationship,
    chargeLineItems
  };
};

const assertAllEqual = (arr: (string | number | null | undefined)[], name: string) => {
  const isAllEqual = arr.every((val) => val === arr[0]);
  const values = JSON.stringify(arr);
  invariant(
    isAllEqual,
    `Expected all shipments to have the same '${name}', but received the following values for '${name}': ${values}`
  );
};
